import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {useFormik} from 'formik';
import {
  forwardRef,
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppSelector} from '../../hooks/redux';
import {Site, SiteListQuery, SiteListResponse} from '../../interfaces/Site';
import reduxSelectors from '../../redux/selectors';
import AccessControl from '../common/AccessControl';
import DataGrid, {DataGridColumn, DataGridRef} from '../common/DataGrid';
import SiteItemDeleteButton from './SiteItemDeleteButtont';
import SiteItemUpsertButton from './SiteItemUpsertButton';
import SitePermissionsEditButton from './SitePermissionsEditButton';

interface Props {}

export interface SiteListRef {
  fetch?: () => void;
  dataGridRef: MutableRefObject<DataGridRef | null>;
}

const SiteList = forwardRef<SiteListRef, Props>((__, ref) => {
  // Ref
  useImperativeHandle(ref, () => ({
    fetch: () => fetchData(formik.values),
    dataGridRef,
  }));

  // Fetch
  const [fetchedData, setFetchedData] = useState<SiteListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const isDarkMode = useAppSelector(reduxSelectors.app.getIsDarkMode);
  const paperBg = isDarkMode ? '#222222' : '#FFF';
  const fetchData = async (params: SiteListQuery) => {
    setFetchedInProgress(true);
    try {
      const resp = await API.get<SiteListResponse>(`${apiBaseUrl}/site`, {
        params,
      });
      setFetchedData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_cc646377');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  // Data Grid
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<Site>[] = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: true,
    },
    {
      field: 'name',
      sortable: true,
    },
    {
      field: 'status',
      sortable: true,
      renderCell: ({row}) => {
        return row.status === 'active' ? (
          <ToggleOnIcon color="success" />
        ) : (
          <ToggleOffIcon color="error" />
        );
      },
    },
    {
      field: 'description',
      sortable: false,
      renderCell: ({row}) => {
        return <Typography noWrap>{row.description}</Typography>;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      sxHeader: {textAlign: 'right'},
      sxCell: {textAlign: 'right'},
      renderCell: ({row}) => {
        return (
          <Box display="flex" gap={1} justifyContent="end">
            <AccessControl permissions={['patch::/site/:id(\\d+)']}>
              <SitePermissionsEditButton
                pk={row.id}
                name={row.name || undefined}
                component={IconButton}
                componentProps={{color: 'primary', size: 'small'}}
                onSubmitted={() => fetchData(formik.values)}
              >
                <PeopleIcon />
              </SitePermissionsEditButton>
            </AccessControl>

            <AccessControl permissions={['patch::/site/:id(\\d+)']}>
              <SiteItemUpsertButton
                pk={row.id}
                item={row}
                prefetch
                component={IconButton}
                componentProps={{color: 'primary', size: 'small'}}
                onSubmitted={() => fetchData(formik.values)}
              >
                <EditIcon />
              </SiteItemUpsertButton>
            </AccessControl>

            <AccessControl permissions={['delete::/site/:id(\\d+)']}>
              <SiteItemDeleteButton
                item={row}
                component={IconButton}
                componentProps={{color: 'error', size: 'small'}}
                onDeleted={() => fetchData(formik.values)}
              >
                <DeleteOutlineOutlinedIcon />
              </SiteItemDeleteButton>
            </AccessControl>
          </Box>
        );
      },
    },
  ];

  const formik = useFormik<SiteListQuery>({
    initialValues: {
      page: 0,
      limit: 25,
      order: 'id',
      dir: 'DESC',
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    fetchData(formik.values);
  }, [formik.values]);

  return (
    <Paper
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        p: 3,
        bgcolor: paperBg,
        backgroundImage: 'none',
      }}
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}
      <DataGrid
        sx={{
          bgcolor: 'unset',
          th: {
            bgcolor: paperBg,
          },
        }}
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={fetchedInProgress}
        pagination
        paginationMode="server"
        size="small"
        sortBy={{
          field: formik.values.order,
          dir: formik.values.dir === 'DESC' ? 'desc' : 'asc',
        }}
        sortingMode="server"
        page={formik.values.page}
        pageSize={formik.values.limit}
        rowCount={fetchedData?.count}
        onPageChange={(v) => formik.setFieldValue('page', v)}
        onPageSizeChange={(v) => {
          formik.setFieldValue('limit', v);
          formik.setFieldValue('page', 0);
        }}
        onSort={(v) => {
          if (v) {
            formik.setFieldValue('order', v.field);
            formik.setFieldValue('dir', v.dir.toUpperCase());
          }
        }}
      />
    </Paper>
  );
});

export default SiteList;
